<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'

export interface TabItem {
  label: string
  fn?: () => void
}

interface Props {
  modelValue?: TabItem
  class?: string
  items: TabItem[]
}

const props = defineProps<Props>()

const active = ref(props.modelValue ?? props.items[0])

const listRef = ref<HTMLElement>()
const activeBg = ref({
  left: 0,
  width: 0
})

const emit = defineEmits(['update:modelValue'])
watch(active, newValue => {
  emit('update:modelValue', newValue)
})

const updateIndicator = () => {
  const container = listRef.value
  if (!container) return

  const activeEl = container.querySelector(`[data-item="${active.value?.label}"]`) as HTMLElement | null

  if (!activeEl) return

  activeBg.value = {
    left: activeEl.offsetLeft,
    width: activeEl.offsetWidth
  }
}

onMounted(() => {
  updateIndicator()
})

window.onresize = () => {
  updateIndicator()
}

watch(active, async () => {
  await nextTick()
  updateIndicator()
})
</script>

<template>
  <div class="inline-flex rounded-full p-px bg-gradient-to-b from-white/20 via-white/10 to-white/5 w-full" :class>
    <ul ref="listRef" class="relative flex items-center rounded-full bg-gray-750 p-1 select-none w-full">
      <li
        aria-hidden="true"
        class="absolute top-1 bottom-1 rounded-full bg-gray-600 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] transition-all duration-300 ease-out"
        :style="{
          left: `${activeBg.left}px`,
          width: `${activeBg.width}px`
        }" />

      <!-- each tab is a button, the active one is marked as pressed -->
      <li v-for="item in items" :key="item.label" :data-item="item.label" class="relative z-10 flex-1 flex">
        <button
          type="button"
          class="w-full cursor-pointer rounded-full px-8 max-md:px-2 py-2 text-sm text-gray-400 flex items-center justify-center"
          :class="{ 'text-gray-300!': active?.label === item.label }"
          :aria-pressed="active?.label === item.label"
          @click="
            () => {
              if (item.fn) item.fn()
              active = item
            }
          ">
          {{ item.label }}
        </button>
      </li>
    </ul>
  </div>
</template>
